import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  TrendingUp, Layers, LineChart, Landmark, Handshake, Sparkles, ShieldCheck, Cog,
  Target, HelpCircle, Rocket, Users, Compass, Database, Eye, Map,
  Award, Scale, Globe, BarChart3, Activity,
  Search,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import StrategyNetwork from "@/components/heroes/StrategyRoadmap";

const MATTERS = [
  { icon: TrendingUp, title: "Business growth outpacing technology", body: "Ambition is running faster than the systems and leadership meant to support it." },
  { icon: Layers, title: "Multiple disconnected systems", body: "Work and information fragment across functions that no longer speak to each other cleanly." },
  { icon: LineChart, title: "Investments without measurable ROI", body: "Technology spend keeps rising while the link to business return stays hard to defend." },
  { icon: Landmark, title: "Lack of governance", body: "Major decisions are made in isolation, without the discipline and accountability the stakes require." },
  { icon: Handshake, title: "Vendor-driven decisions", body: "The technology agenda gets shaped by suppliers rather than by the business itself." },
  { icon: Cog, title: "Technology complexity", body: "The organisation is running more platforms and partners than any one leader can hold together." },
  { icon: Sparkles, title: "AI readiness", body: "Pressure to adopt AI without a clear, commercial view of where it actually belongs in the business." },
  { icon: ShieldCheck, title: "Operational inefficiencies", body: "Cost and effort keep rising while value to the business barely moves." },
];

const QUESTIONS = [
  { icon: Target, title: "Is our technology aligned with our business goals?", body: "Does the technology agenda actually serve the plan the leadership team is executing?" },
  { icon: LineChart, title: "Are we investing in the right priorities?", body: "Where is capital going, and is that where it earns the strongest business return?" },
  { icon: Compass, title: "Which technology initiatives should come first?", body: "What are the two or three decisions that most shape the next stage of the business?" },
  { icon: Sparkles, title: "How do we prepare for AI?", body: "Where can AI create genuine business value, and what does the organisation need to do before it commits?" },
  { icon: ShieldCheck, title: "How do we reduce technology-related business risk?", body: "Where are the largest exposures, and what will bring them under control?" },
  { icon: Eye, title: "How do we improve executive visibility?", body: "How does the leadership team get a clean, board-level view of value, cost and risk?" },
  { icon: Rocket, title: "Are our current systems supporting growth?", body: "Will the operating core carry the business through its next stage, or cap it?" },
  { icon: Map, title: "How do we create a long-term technology roadmap?", body: "What does a defensible, board-ready five-to-ten year plan for the business actually look like?" },
];

const FRAMEWORK = [
  { title: "Business vision", body: "Start with the business the leadership team is trying to build over the next five to ten years, and why that matters commercially." },
  { title: "Current state assessment", body: "A candid, unblinking read on where the business really is: capability, cost, risk and maturity of the operating core." },
  { title: "Business and technology alignment", body: "Bring the technology direction into line with the business plan, and defend the join at board level." },
  { title: "Priority identification", body: "The two or three moves that will most shape the next stage of the business are identified, sequenced and defended." },
  { title: "Technology roadmap", body: "A prioritised, board-ready plan that turns strategy into decisions the business can execute and stand behind." },
  { title: "Governance model", body: "Decision rights, oversight and reporting proportionate to the scale of the ambition." },
  { title: "Execution oversight", body: "Delivery, teams and vendors held to account against the business outcome, not the project plan." },
  { title: "Business outcomes review", body: "Results measured in the board's own terms: growth, margin, resilience and risk reduced." },
];

const FOCUS = [
  { icon: Target, title: "Business & Technology Alignment", body: "The point where the business plan and the technology agenda become the same plan." },
  { icon: LineChart, title: "Technology Investment Planning", body: "Where the capital goes, in what order and against what defensible return." },
  { icon: Database, title: "Enterprise Applications Strategy", body: "A coherent view of the core platforms that carry the business, and how they evolve." },
  { icon: Layers, title: "ERP & SAP Advisory", body: "Independent leadership on the largest and riskiest core platform decisions the business will make." },
  { icon: Sparkles, title: "AI Strategy", body: "A grounded view of where AI creates real business value, and where it does not." },
  { icon: Landmark, title: "Technology Governance", body: "The oversight and decision rights that keep major choices accountable to the board." },
  { icon: ShieldCheck, title: "Cybersecurity Oversight", body: "Board-level visibility of cyber exposure, and the discipline to manage it well." },
  { icon: Handshake, title: "Vendor Evaluation", body: "Objective evaluation of partners and providers, free of commercial bias or hidden incentives." },
  { icon: Activity, title: "Business Continuity", body: "Confidence that the operating core keeps running as the business grows and conditions change." },
  { icon: Map, title: "Technology Roadmaps", body: "A prioritised, board-ready plan that turns strategy into technology the business can execute." },
];

const OUTCOMES = [
  { title: "Business Growth", note: "New revenue, markets and capacity." },
  { title: "Operational Excellence", note: "A leaner, more resilient core." },
  { title: "Investment Optimisation", note: "Spend tied to a defensible return." },
  { title: "Executive Visibility", note: "A clear board-level line of sight." },
  { title: "Improved Governance", note: "Decisions held to account." },
  { title: "Risk Reduction", note: "Cyber and delivery risk controlled." },
  { title: "Scalable Operations", note: "Systems built for the next stage." },
  { title: "Competitive Advantage", note: "Capability rivals cannot easily copy." },
];

const WHY = [
  { icon: Award, title: "27+ years executive leadership", body: "Judgement built in real boardrooms, on real programmes, with real commercial consequence, not theory." },
  { icon: Target, title: "Business-first perspective", body: "Every recommendation begins with the business result it is meant to create. Technology follows the strategy, never the other way around." },
  { icon: Scale, title: "Independent executive advice", body: "No products to sell and no platforms to push. The only interest is the decision that is right for your business." },
  { icon: Handshake, title: "Vendor-neutral recommendations", body: "Objective evaluation of partners, platforms and providers, free of commercial bias or hidden incentives." },
  { icon: Users, title: "Fractional CIO leadership", body: "Senior, board-level technology leadership on the cadence your business requires, without a permanent appointment." },
  { icon: Compass, title: "Practical technology strategy", body: "A grounded plan the business can genuinely execute, not a document that looks impressive and moves nothing." },
  { icon: Globe, title: "Experience across India, the UAE and the Gulf", body: "Direct experience of business conditions across the region, and how technology decisions play out in practice." },
  { icon: BarChart3, title: "Long-term strategic partnership", body: "A senior advisor who stays through delivery, accountable to the outcome, not just the advice." },
];

const PRINCIPLES = [
  "Technology should always support business objectives.",
  "Business strategy comes before technology decisions.",
  "Every technology investment should create measurable value.",
  "Governance is essential for sustainable growth.",
  "Technology should simplify operations, not increase complexity.",
  "Leadership alignment is the foundation of successful transformation.",
];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Technology Strategy Advisory",
  serviceType: "Executive Technology Strategy Advisory",
  description: "Practical technology strategies for growing businesses, mid-market companies and family-owned enterprises. Aftab Azad helps executive teams across India, the UAE and the Gulf Region align technology investments with business priorities and measurable outcomes.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/services/technology-strategy",
};

export default function TechnologyStrategy() {
  return (
    <>
      <Seo
        title="Technology Strategy Advisory"
        description="Technology strategy that supports business growth. Aftab Azad helps executive teams in growing businesses across India, the UAE and the Gulf Region align technology investments with business priorities and measurable outcomes."
        path="/services/technology-strategy"
        keywords={["Technology Strategy Advisor", "Technology Strategy Consultant", "Executive Technology Advisor", "Fractional CIO", "Business Technology Strategy", "Mid-Market Technology Strategy", "Family-Owned Business Advisor"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="ts-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <StrategyNetwork className="pointer-events-none absolute inset-y-10 right-0 hidden h-[calc(100%-5rem)] w-[38%] max-w-[520px] pr-4 opacity-90 md:block lg:w-[40%]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">Technology Strategy</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["Technology strategy that", "supports"]} lineClassName="font-light" />
            <KineticHeading lines={["business growth."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            Technology should never operate independently of business strategy. As organisations grow,
            leadership teams need a clear technology direction that supports operational excellence,
            business expansion, better decision-making and long-term competitiveness. Aftab Azad helps
            business leaders develop practical technology strategies that align investments with
            business priorities across India, the UAE and the Gulf Region.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="ts-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Book Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/contact" data-testid="ts-hero-discuss"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Discuss Your Technology Strategy
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHY IT MATTERS */}
      <section data-testid="ts-matters" className="bg-surface py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why technology strategy matters</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Investment without", "a strategy."]} />
            </h2>
            <p className="mt-5 max-w-[680px] font-body text-lg leading-relaxed text-slate-600">
              Many growing businesses invest in technology without a long-term strategy. Strategy should
              always precede investment. Without it, the same problems return in different forms.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {MATTERS.map((m, i) => {
              const Icon = m.icon;
              return (
                <Reveal key={m.title} delay={(i % 4) * 0.05}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-bold leading-snug tracking-tight text-navy">{m.title}</h3>
                    <p className="mt-3 font-body text-sm leading-relaxed text-slate-600">{m.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — COMMON EXECUTIVE QUESTIONS */}
      <section data-testid="ts-questions" className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Common executive questions</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The questions", "asked at every", "board table."]} />
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {QUESTIONS.map((q, i) => {
              const Icon = q.icon;
              return (
                <Reveal key={q.title} delay={(i % 2) * 0.05}>
                  <div className="flex h-full gap-5 rounded-[1.5rem] border border-slate-200 bg-surface p-7">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold leading-snug tracking-tight text-navy">{q.title}</h3>
                      <p className="mt-2 font-body leading-relaxed text-slate-600">{q.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — FRAMEWORK */}
      <section data-testid="ts-framework" className="bg-surface py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Technology strategy framework</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["From vision to", "measurable outcome."]} />
            </h2>
          </div>
          <div className="mt-12 max-w-3xl">
            {FRAMEWORK.map((s, i) => (
              <Reveal key={s.title} className="relative flex gap-6 pb-8 last:pb-0">
                {i < FRAMEWORK.length - 1 && <span aria-hidden className="absolute left-6 top-14 h-[calc(100%-2.5rem)] w-px bg-slate-200" />}
                <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-navy font-heading text-lg font-bold text-white">{i + 1}</div>
                <div className="pt-1.5">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-2 max-w-[680px] font-body leading-relaxed text-slate-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — AREAS OF STRATEGIC FOCUS */}
      <section data-testid="ts-focus" className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Areas of strategic focus</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Where strategy", "meets the business."]} />
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {FOCUS.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={(i % 2) * 0.04}>
                  <div className="flex h-full gap-5 rounded-[1.5rem] border border-slate-200 bg-surface p-7">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold leading-snug tracking-tight text-navy">{f.title}</h3>
                      <p className="mt-2 font-body leading-relaxed text-slate-600">{f.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6 — BUSINESS OUTCOMES */}
      <section data-testid="ts-outcomes" className="relative overflow-hidden bg-navy py-20 text-white md:py-24 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 md:grid-cols-4">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-lg font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHY WORK WITH AFTAB */}
      <section data-testid="ts-why" className="bg-surface py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why business leaders work with Aftab Azad</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The difference", "in the room."]} />
            </h2>
          </div>
          <div className="mt-12 grid gap-x-10 gap-y-10 md:grid-cols-2">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} className="flex gap-5 border-t border-slate-200 pt-6">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{w.title}</h3>
                    <p className="mt-2 max-w-[520px] font-body text-lg leading-relaxed text-slate-600">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 8 — PRINCIPLES */}
      <section data-testid="ts-principles" className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Technology strategy principles</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Convictions we", "return to."]} />
            </h2>
          </div>
          <div className="mt-12 grid gap-x-10 gap-y-8 md:grid-cols-2">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p} className="relative border-t border-slate-200 pt-5">
                <span className="font-body text-sm font-bold text-royal">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy">{p}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — CLOSING */}
      <section data-testid="ts-closing" className="relative overflow-hidden bg-navy py-20 text-white md:py-24 grain-overlay">
        <div className="pointer-events-none absolute -right-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">A closing thought</p>
          <blockquote className="mt-6 font-heading text-3xl font-light leading-[1.3] tracking-tight md:text-4xl">
            The strongest technology strategies begin with a clear understanding of the business. When
            technology investments align with business priorities, organisations become more resilient,
            more efficient and better prepared for future growth.
          </blockquote>
        </div>
      </section>

      <CTASection
        overline="Build a technology strategy"
        heading={["For your next stage", "of growth."]}
        body="Whether your organisation is preparing for expansion, evaluating AI opportunities, modernising enterprise applications or looking for experienced executive technology leadership, let's discuss how a practical technology strategy can strengthen your business."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Explore Advisory Services", to: "/services/executive-advisory" }}
      />
    </>
  );
}
